import { useState } from 'react';
import { Banner, Button, Modal, StatusBadge } from '@immo/ui-kit';
import type { LandIqDataRun, LandIqDataRunEntry, ResumeLandIqDataRunResponse } from '@immo/api-contract';
import { apiClient } from '@/lib/auth/api-client';
import { formatCommandLines } from '@/lib/landiq/format-command';
import { useTranslations } from '@/i18n/use-translations';
import { AlertCircleIcon, PlayIcon, WarningTriangleIcon } from '@/components/layout/icons';
import { LANDIQ_PAGE_ERROR_CODES } from '@/components/landiq/landiq-error-state';
import { StartRefusalBanner } from '@/components/landiq/start-refusal-banner';

type Refusal = Extract<ResumeLandIqDataRunResponse, { outcome: 'REFUSED' }>;

export type ResumeOutcome =
  | { kind: 'started'; run: LandIqDataRun }
  | { kind: 'refused'; refusal: Refusal }
  | { kind: 'failed'; code: string | undefined };

type Props = {
  readonly entry: LandIqDataRunEntry;
  readonly environment: string;
  readonly onClose: () => void;
  readonly onSettled: (outcome: ResumeOutcome) => void;
};

type State = { kind: 'idle' } | { kind: 'sending' } | Exclude<ResumeOutcome, { kind: 'started' }>;

export function ResumeDataRunDialog({ entry, environment, onClose, onSettled }: Props) {
  const t = useTranslations('pages.admin.landiq-data-run');
  const tList = useTranslations('pages.admin.landiq-data-runs');
  const tErrors = useTranslations('errors');
  const [state, setState] = useState<State>({ kind: 'idle' });
  const sending = state.kind === 'sending';

  async function submit() {
    if (state.kind !== 'idle') return;

    setState({ kind: 'sending' });

    try {
      const result = await apiClient.adminLandiqDataRuns.resume({ pathParams: { entryId: entry.id } });

      if (!result.ok) {
        const code = (result.body as { error?: { code?: string } } | undefined)?.error?.code;
        setState({ kind: 'failed', code });
        onSettled({ kind: 'failed', code });

        return;
      }

      if (result.body.outcome === 'STARTED') {
        onSettled({ kind: 'started', run: result.body.run });

        return;
      }

      setState({ kind: 'refused', refusal: result.body });
      onSettled({ kind: 'refused', refusal: result.body });
    } catch {
      setState({ kind: 'failed', code: undefined });
      onSettled({ kind: 'failed', code: undefined });
    }
  }

  const settled = state.kind === 'refused' || state.kind === 'failed';

  return (
    <Modal
      open
      onClose={() => (sending ? undefined : onClose())}
      confirmOnClose={false}
      size="md"
      title={
        <span className="inline-flex items-center gap-2">
          <PlayIcon aria-hidden className="h-4 w-4" />
          {t('resume-dialog.title')}
        </span>
      }
      description={entry.id}
      footer={
        <>
          <Button variant="secondary" type="button" onClick={onClose} disabled={sending}>
            {t('resume-dialog.cancel')}
          </Button>
          {!settled && (
            <Button variant="primary" type="button" onClick={() => void submit()} loading={sending} disabled={sending}>
              {t('resume-dialog.submit')}
            </Button>
          )}
        </>
      }
    >
      <p className="text-sm text-text-secondary">{t('resume-dialog.body', { environment })}</p>
      <div className="mt-3 rounded-md border border-border bg-canvas px-3 py-2 font-mono text-sm text-text-primary">
        {formatCommandLines(entry.command, entry.args).map((line, index) => (
          <div key={`${index}:${line}`} className={index === 0 ? undefined : 'pl-4'}>
            {line}
          </div>
        ))}
      </div>
      <div className="mt-3">
        <StatusBadge label={tList('confirm.environment', { environment })} tone="neutral" size="sm" shape="square" />
      </div>
      {state.kind === 'refused' && (
        <div className="mt-4">
          <RefusalBanner refusal={state.refusal} entryId={entry.id} />
        </div>
      )}
      {state.kind === 'failed' && (
        <div className="mt-4">
          <Banner
            variant="danger"
            icon={<AlertCircleIcon />}
            title={tErrors(LANDIQ_PAGE_ERROR_CODES.find((code) => code === state.code) ?? 'generic.unexpected')}
          />
        </div>
      )}
    </Modal>
  );
}

function RefusalBanner({ refusal, entryId }: { readonly refusal: Refusal; readonly entryId: string }) {
  const t = useTranslations('pages.admin.landiq-data-run');
  const { reason } = refusal;

  if (reason === 'RESUME_NOT_POSSIBLE') {
    return (
      <Banner
        variant="warning"
        icon={<WarningTriangleIcon />}
        title={t('resume-dialog.refusals.RESUME_NOT_POSSIBLE')}
      />
    );
  }

  return <StartRefusalBanner refusal={{ ...refusal, reason }} entryId={entryId} />;
}
